import axios from 'axios';
import { getApiUrl } from '../utils/apiConfig';

/**
 * Get professional's earnings summary
 * @returns {Promise} Earnings data including total, pending, paid out amounts
 */
export const getProfessionalEarnings = async () => {
  try {
    const token = localStorage.getItem('token');

    if (!token) {
      throw new Error('Authentication required');
    }

    console.log('📡 Calling API:', getApiUrl('/payouts/earnings'));

    const response = await axios.get(getApiUrl('/payouts/earnings'), {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    console.log('📡 API Response:', response);

    return response.data;
  } catch (error) {
    console.error('Error fetching earnings:', error);
    console.error('Error details:', error.response?.data);
    throw error;
  }
};

/**
 * Get payout history for professional
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number (default: 1)
 * @param {number} params.limit - Items per page (default: 10)
 * @param {string} params.status - Filter by status (optional)
 * @returns {Promise} Payout history with pagination
 */
export const getPayoutHistory = async (params = {}) => {
  try {
    const token = localStorage.getItem('token');

    if (!token) {
      throw new Error('Authentication required');
    }

    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.status) queryParams.append('status', params.status);

    const response = await axios.get(
      getApiUrl(`/payouts/history?${queryParams.toString()}`),
      {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }
    );

    return response.data;
  } catch (error) {
    console.error('Error fetching payout history:', error);
    throw error;
  }
};

/**
 * Get detailed payout information
 * @param {string} payoutId - Payout ID
 * @returns {Promise} Detailed payout data including all payments
 */
export const getPayoutDetails = async (payoutId) => {
  try {
    const token = localStorage.getItem('token');

    if (!token) {
      throw new Error('Authentication required');
    }

    const response = await axios.get(getApiUrl(`/payouts/${payoutId}`), {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    return response.data;
  } catch (error) {
    console.error('Error fetching payout details:', error);
    throw error;
  }
};

/**
 * Request manual payout
 * @returns {Promise} Payout request response
 */
export const requestPayout = async () => {
  try {
    const token = localStorage.getItem('token');

    if (!token) {
      throw new Error('Authentication required');
    }

    const response = await axios.post(
      getApiUrl('/payouts/request'),
      {},
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );

    return response.data;
  } catch (error) {
    console.error('Error requesting payout:', error);
    throw error;
  }
};

/**
 * Format currency amount for display
 * @param {number} amount - Amount in pence
 * @param {string} currency - Currency code (default: gbp)
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, currency = 'gbp') => {
  const value = amount / 100;

  const formatter = new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

  return formatter.format(value);
};

/**
 * Calculate commission breakdown
 * @param {number} grossAmount - Gross amount in pence
 * @param {number} feePercentage - Fee percentage (default: 0.10 for 10%)
 * @returns {Object} Commission breakdown
 */
export const calculateCommission = (grossAmount, feePercentage = 0.10) => {
  const platformFee = Math.round(grossAmount * feePercentage);
  const netAmount = grossAmount - platformFee;

  return {
    grossAmount,
    platformFee,
    netAmount,
    feePercentage
  };
};
